import { EventEmitter, Injectable } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MatDialogState,
} from '@angular/material/dialog';
import { MakePaymentComponent } from '@shared/mat-dialogs/make-payment/make-payment.component';
import { AddMoneyComponent } from '@shared/mat-dialogs/add-money/add-money.component';
import { RequestMoneyComponent } from '@shared/mat-dialogs/request-money/request-money.component';
import { WithdrawMoneyComponent } from '@shared/mat-dialogs/withdraw-money/withdraw-money.component';
import { CreditPaymentComponent } from '@shared/mat-dialogs/credit-payment/credit-payment.component';
import { ParamsService } from './params.service';
import { InvoiceFundsComponent } from '@shared/mat-dialogs/invoice-funds/invoice-funds.component';
import { CreateProfileComponent } from '@shared/mat-dialogs/invoice-funds/create-profile/create-profile.component';
import { CommissionsComponent } from '@shared/mat-dialogs/commissions/commissions.component';
import { CommissionCreateProfileComponent } from '@shared/mat-dialogs/commissions/create-profile/create-profile.component';
import { ConvertFundsComponent } from '@shared/mat-dialogs/convert-funds/convert-funds.component';
import { StudentPaymentsComponent } from '@shared/mat-dialogs/student-payment/student-payments.component';
import { DeletePaymentsComponent } from '@shared/mat-dialogs/delete-payments/delete-payments.component';
import { ComponentType } from '@angular/cdk/portal';
import { finalize, Observable, Subscription } from 'rxjs';
import { ActionConfirmationComponent } from '@shared/mat-dialogs/action-confirmation/action-confirmation.component';
import { InviteNewUserComponent } from '@shared/mat-dialogs/invite-new-user/invite-new-user.component';
import { DeactivateUserComponent } from '@shared/mat-dialogs/deactivate-user/deactivate-user.component';
import { EditUserNameComponent } from '@shared/mat-dialogs/edit-user-name/edit-user-name.component';
import { RegisterEmailComponent } from '@shared/mat-dialogs/register-email/register-email.component';
import { CustomSpreadDialogComponent } from '@shared/mat-dialogs/custom-spread-dialog/custom-spread-dialog.component';
import { PaymentCustomSpreadComponent } from '@shared/mat-dialogs/payment-custom-spread/payment-custom-spread.component';

type MappedComponents<T = any> = Map<string, [MatDialogRef<T>, Subscription]>;

const container: MappedComponents = new Map();

@Injectable({
  providedIn: 'root',
})
export class MatDialogService {
  classDialog: MatDialogConfig = {
    width: '95vw',
    height: '95vh',
    panelClass: 'sm-full',
    data: null,
    disableClose: true,
  };

  constructor(private MD: MatDialog, private PRMS: ParamsService) {}

  open<T, D>(
    Component: ComponentType<T>,
    config: MatDialogConfig<D>,
  ): Observable<any> {
    const instance = container.get(Component.name);
    const ref = this.MD.open(Component, config);

    const subscription = ref.afterOpened().subscribe(() => {
      if (instance) {
        const [oldRef, oldSubscription] = instance;
        if (oldRef.getState() === MatDialogState.OPEN) {
          oldRef.close();
        }
        oldSubscription.unsubscribe();
      }

      container.delete(Component.name);
      container.set(Component.name, [ref, subscription]);
    });

    return ref.afterClosed().pipe(
      finalize(() => {
        subscription.unsubscribe();
        container.delete(Component.name);
      }),
    );
  }

  /**
   * start the process for make payment.
   * @author Martin Batun Tec.
   */
  makePayment(): void {
    this.open(MakePaymentComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  /**
   * start the process for make payment.
   * @author Martin Batun Tec.
   */
  creditPayment(data?: any): void {
    if (data) {
      this.classDialog.data = data;
    }
    this.open(CreditPaymentComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
      this.PRMS.get.next(true);
    });
  }

  /**
   * start the process for add money.
   * @author Martin Batun Tec.
   */
  addMoney(): void {
    this.open(AddMoneyComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  /**
   * start the process for request money.
   * @author Martin Batun Tec.
   */
  requestMoney(): void {
    this.open(RequestMoneyComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  studentPayment(): void {
    this.open(StudentPaymentsComponent, this.classDialog).subscribe(
      (result) => {
        if (!result) {
          return;
        }
      },
    );
  }

  deletePayment(
    paymentId: string | number,
    type: 'PAYMENT_REQUEST' | 'CORPORATE_PAYMENT' | 'COMMISSION' | null = null,
  ): void {
    this.open(DeletePaymentsComponent, {
      ...this.classDialog,
      data: { paymentId, type },
      maxHeight: '690px',
      maxWidth: '700px',
    }).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  confirmationModal(
    title: string,
    message: string,
    proceedButton: string = 'Proceed',
    cancelButton: string = 'Cancel',
    subscription: EventEmitter<any>,
  ): void {
    this.open(ActionConfirmationComponent, {
      ...this.classDialog,
      data: {
        title,
        message,
        proceedButton,
        cancelButton,
        eventManager: subscription,
      },
      maxHeight: '550px',
      maxWidth: '680px',
    }).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  /**
   * start the process for withdraw money.
   * @author Martin Batun Tec.
   */
  withdrawMoney(data?: any, callback?: any): void {
    if (data) {
      this.classDialog.data = data;
    }
    this.open(WithdrawMoneyComponent, this.classDialog).subscribe((result) => {
      callback?.();
      if (!result) {
        return;
      }
    });
  }

  invoiceFunds(): void {
    this.open(InvoiceFundsComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  createInvoiceProfile(): void {
    this.open(CreateProfileComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  commissions(): void {
    this.open(CommissionsComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  createCommissionProfile(): void {
    this.open(CommissionCreateProfileComponent, this.classDialog).subscribe(
      (result) => {
        if (!result) {
          return;
        }
      },
    );
  }

  convertFunds(): void {
    this.open(ConvertFundsComponent, this.classDialog).subscribe((result) => {
      if (!result) {
        return;
      }
    });
  }

  inviteUser(): Observable<any> {
    return this.open(InviteNewUserComponent, {
      ...this.classDialog,
      maxWidth: '700px',
      maxHeight: '495px',
    });
  }

  deactivateUser(userId: string, email: string): void {
    this.open(DeactivateUserComponent, {
      ...this.classDialog,
      maxWidth: '700px',
      maxHeight: '320px',
      height: 'auto',
      data: { userId, email },
    }).subscribe((result) => {
      if (!result) {
        return;
      }
      this.PRMS.get.next(true);
    });
  }

  editUserName(
    userId: string,
    email: string,
    userName?: string,
  ): Observable<any> {
    return this.open(EditUserNameComponent, {
      ...this.classDialog,
      maxWidth: '700px',
      maxHeight: '450px',
      height: 'auto',
      data: { userId, userName, email },
    });
  }

  /**
   * Opens the register email modal for invoice profiles
   * @param profile The invoice profile to register an email for
   */
  registerEmail(profile: any): void {
    this.open(RegisterEmailComponent, {
      ...this.classDialog,
      data: { profile },
      maxHeight: '320px',
      maxWidth: '700px',
    }).subscribe((result) => {
      if (!result) {
        return;
      }
      this.PRMS.get.next(true);
    });
  }

  setupCustomSpread(): Observable<any> {
    return this.open(CustomSpreadDialogComponent, {
      ...this.classDialog,
      maxWidth: '900px',
      maxHeight: '600px',
    });
  }

  editPaymentCustomSpread(paymentRequestId: string): Observable<any> {
    return this.open(PaymentCustomSpreadComponent, {
      ...this.classDialog,
      maxWidth: '1100px',
      maxHeight: '650px',
      data: { paymentRequestId },
    });
  }
}
